import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { FORM_LOCK_SERVICE_TOKEN } from '@crowdctrl/shared';

/**
 * Guard to prevent navigation when form is locked.
 * Only allows navigation to the exact locked form route.
 * Redirects to the locked form if user tries to navigate elsewhere.
 */
export const formLockGuard: CanActivateFn = (route, state) => {
  const formLockService = inject(FORM_LOCK_SERVICE_TOKEN, { optional: true });
  const router = inject(Router);

  // If service is not available (not in forms app), allow navigation
  if (!formLockService) {
    return true;
  }

  // If form is not locked, allow all navigation
  if (!formLockService.isLocked()) {
    return true;
  }

  // Form is locked - get the stored route
  const lockedFormRoute = formLockService.getLockedFormRoute();
  if (!lockedFormRoute) {
    // Locked but no stored route - clear lock and allow navigation (safety fallback)
    formLockService.unlock();
    return true;
  }

  // Check if trying to access the exact locked form route
  const targetUrl = state.url;
  const expectedFormUrl = `/forms/${lockedFormRoute.workspaceId}/${lockedFormRoute.sessionId}/${lockedFormRoute.formId}`;

  if (targetUrl === expectedFormUrl) {
    return true; // Allow navigation to the exact locked form
  }

  // Form is locked and not on the locked form - redirect to it
  router.navigateByUrl(expectedFormUrl, { replaceUrl: true });
  return false; // Block current navigation
};
